import { useState, useCallback, useEffect, useRef, memo, useMemo } from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils.ts'
import { ChevronUp, ChevronDown, GripVertical, Layers, Plus, Trash2, LayoutList, StickyNote, PackagePlus, X, Sigma, Book, Search, Loader2 } from 'lucide-react'
import { Button, EmptyState, ErrorState, LoadingState } from '@/components/ui/index.ts'
import { apiClient } from '@/lib/api.ts'
import { hasHtmlTags, sanitizeQuoteHtml } from '@/lib/safeHtml.ts'
import {
  createQuotePosition,
  updateQuotePosition,
  deleteQuotePosition,
  type QuotePosition,
} from '@/api/quotes.ts'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useQuery } from '@tanstack/react-query'
import { ItemFormModal } from '@/components/service-catalog/ItemFormModal.tsx'
import type { ServiceCatalogItem as FullCatalogItem, ServiceCatalogCategory } from '@/components/service-catalog/ItemFormModal.tsx'

// ─── Styles ──────────────────────────────────────────────────────────────────

const rowStyles = cva('group flex items-start gap-2 py-2 border-b border-gray-100 last:border-0', {
  variants: {
    kind: {
      article: '',
      service: '',
      note: 'bg-amber-50/60',
      subtotal: 'bg-blue-50/60 border-blue-100',
    },
  },
  defaultVariants: { kind: 'article' },
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(val: string | number | null | undefined): string {
  if (val === null || val === undefined || val === '') return '—'
  const n = typeof val === 'string' ? parseFloat(val) : val
  if (isNaN(n)) return '—'
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n)
}

function calcLineTotal(pos: QuotePosition): number | null {
  if (pos.kind === 'note' || pos.kind === 'subtotal') return null
  if (pos.unitPrice === null || pos.unitPrice === undefined) return null
  const qty = parseFloat(String(pos.quantity))
  const price = parseFloat(String(pos.unitPrice))
  const disc = parseFloat(String(pos.discountPct ?? 0))
  if (isNaN(qty) || isNaN(price)) return null
  return qty * price * (1 - disc / 100)
}

function calcNetTotal(positions: QuotePosition[]): number {
  // When a BOM parent has a bundle price, skip its children to avoid double-counting.
  // A parent with unitPrice=null means pricing is at child level — children are included.
  const parentIdsWithPrice = new Set(
    positions
      .filter(p => !p.parentPositionId && p.kind !== 'note' && p.kind !== 'subtotal' && p.unitPrice !== null && parseFloat(String(p.unitPrice)) > 0)
      .map(p => p.id),
  )
  return positions.reduce((sum, p) => {
    if (p.parentPositionId && parentIdsWithPrice.has(p.parentPositionId)) return sum
    const line = calcLineTotal(p)
    return sum + (line ?? 0)
  }, 0)
}

function calcSubtotalAmount(pos: QuotePosition, allPositions: QuotePosition[]): number | null {
  const posIndex = allPositions.findIndex(p => p.id === pos.id)
  if (posIndex === -1) return null
  let startIndex = 0
  for (let i = posIndex - 1; i >= 0; i--) {
    if (allPositions[i].kind === 'subtotal') { startIndex = i + 1; break }
  }
  const slice = allPositions.slice(startIndex, posIndex)
  const parentIdsWithPrice = new Set(
    slice
      .filter(p => !p.parentPositionId && p.kind !== 'note' && p.kind !== 'subtotal' && p.unitPrice !== null && parseFloat(String(p.unitPrice)) > 0)
      .map(p => p.id),
  )
  return slice.reduce((sum, p) => {
    if (p.parentPositionId && parentIdsWithPrice.has(p.parentPositionId)) return sum
    return sum + (calcLineTotal(p) ?? 0)
  }, 0)
}

// ─── Inline-editable number field ────────────────────────────────────────────

interface InlineNumberProps {
  value: string | number | null
  onChange: (val: number) => void
  placeholder?: string
  min?: number
  step?: number
  className?: string
}

function InlineNumber({ value, onChange, placeholder = '0', min = 0, step = 1, className }: InlineNumberProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  function start() {
    setDraft(value !== null && value !== undefined ? String(value) : '')
    setEditing(true)
  }

  function commit() {
    const n = parseFloat(draft)
    if (!isNaN(n) && n !== parseFloat(String(value))) onChange(n)
    setEditing(false)
  }

  if (editing) {
    return (
      <input
        type="number"
        value={draft}
        min={min}
        step={step}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') setEditing(false)
        }}
        className={cn(
          'w-full rounded border border-brand-400 px-1 py-0.5 text-right text-small focus:outline-none focus:ring-1 focus:ring-brand-500',
          className,
        )}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={start}
      title="Klicken zum Bearbeiten"
      className={cn(
        'w-full text-right text-small rounded hover:bg-brand-50 hover:ring-1 hover:ring-brand-200 px-1 py-0.5 transition-colors cursor-text',
        className,
      )}
    >
      {value !== null && value !== undefined && String(value) !== '' ? String(value) : (
        <span className="text-gray-400 italic">{placeholder}</span>
      )}
    </button>
  )
}

// ─── Inline-editable text area ───────────────────────────────────────────────

interface InlineTextAreaProps {
  value: string
  onChange: (val: string) => void
  placeholder?: string
}

function InlineTextArea({ value, onChange, placeholder }: InlineTextAreaProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  function start() {
    setDraft(value)
    setEditing(true)
  }

  function commit() {
    if (draft !== value) onChange(draft)
    setEditing(false)
  }

  if (editing) {
    return (
      <textarea
        value={draft}
        autoFocus
        rows={3}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setEditing(false)
        }}
        className="w-full rounded border border-brand-400 px-1.5 py-1 text-small focus:outline-none focus:ring-1 focus:ring-brand-500 resize-none"
      />
    )
  }

  const isHtml = hasHtmlTags(value)

  if (isHtml) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={start}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') start() }}
        title="Klicken zum Bearbeiten"
        className="w-full text-left text-small rounded hover:bg-brand-50 hover:ring-1 hover:ring-brand-200 px-1.5 py-1 transition-colors cursor-text min-h-[2rem] break-words"
        dangerouslySetInnerHTML={{ __html: sanitizeQuoteHtml(value) }}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={start}
      title="Klicken zum Bearbeiten"
      className="w-full text-left text-small rounded hover:bg-brand-50 hover:ring-1 hover:ring-brand-200 px-1.5 py-1 transition-colors cursor-text min-h-[2rem] whitespace-pre-wrap break-words"
    >
      {value || <span className="text-gray-400 italic text-[10px]">{placeholder ?? '—'}</span>}
    </button>
  )
}

// ─── Position row ─────────────────────────────────────────────────────────────

interface PositionRowProps {
  pos: QuotePosition
  isFirst: boolean
  isLast: boolean
  onUpdate: (positionId: string, patch: Partial<QuotePosition>) => void
  onDelete: (positionId: string) => void
  onMoveUp: (positionId: string) => void
  onMoveDown: (positionId: string) => void
  /** ID der Position, die gerade gespeichert wird — nur diese eine Row zeigt Disabled-State */
  savingId: string | null
  /** Wenn true: Position ist Kind einer Stueckliste → eingerueckt + dezenter Stil */
  isChild?: boolean
  /** Alle Positionen des Quotes — fuer das „Zu Stueckliste zuordnen"-Dropdown.
   *  PositionRow filtert intern: nur andere Top-Level-Positionen sind Kandidaten. */
  allPositions: QuotePosition[]
  /** Anzahl der Kinder dieser Position (nur fuer Eltern-Positionen). */
  childCount?: number
  /** Drag-Handle-Listeners von @dnd-kit/sortable — nur fuer Top-Level-Positionen. */
  dragHandleListeners?: Record<string, unknown>
}

const PositionRow = memo(function PositionRow({ pos, isFirst, isLast, onUpdate, onDelete, onMoveUp, onMoveDown, savingId, isChild, allPositions, childCount = 0, dragHandleListeners }: PositionRowProps) {
  const saving = savingId === pos.id
  const lineTotal = calcLineTotal(pos)
  const [expanded, setExpanded] = useState(false)
  // Local optimistic state for the optional flag — prevents the controlled
  // checkbox from bouncing back to false while the API call is in flight
  // (setSaving(true) in the parent triggers a re-render before refetch).
  const [localOptional, setLocalOptional] = useState(pos.isOptional ?? false)
  useEffect(() => { setLocalOptional(pos.isOptional ?? false) }, [pos.isOptional])
  // Moegliche Parents: alle anderen Positionen des Quotes, die selbst kein
  // Kind sind und nicht die Position selbst. Notiz-Positionen schliessen wir
  // aus (Stueckliste ist ein Artikel/Service, kein reiner Hinweis).
  const parentCandidates = allPositions.filter(
    (p) => p.id !== pos.id && !p.parentPositionId && p.kind !== 'note' && p.kind !== 'subtotal',
  )
  // Primary label is always the description title (first line).
  // The SAP item code is shown as a secondary badge below.
  const descriptionTitle = (pos.description?.split('\n')[0] ?? '').trim() || '—'

  return (
    <div
      className={cn(
        rowStyles({ kind: pos.kind }),
        'px-3',
        // Kind einer Stueckliste: links eingerueckt + linker Balken zeigt
        // Zugehoerigkeit zum Parent-Eintrag. Hintergrund minimal dezenter.
        isChild && 'pl-8 bg-gray-50/60 border-l-2 border-brand-200',
        !isChild && 'select-none',
      )}
    >
      {/* Sort controls */}
      <div className="flex flex-col gap-0.5 pt-0.5 flex-shrink-0 items-center">
        {!isChild && (
          <span
            title="Ziehen zum Verschieben"
            className="cursor-grab active:cursor-grabbing touch-none"
            {...(dragHandleListeners as React.HTMLAttributes<HTMLSpanElement>)}
          >
            <GripVertical className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 mb-0.5 transition-colors" />
          </span>
        )}
        <button
          type="button"
          disabled={isFirst || saving}
          onClick={() => onMoveUp(pos.id)}
          className="text-gray-300 hover:text-gray-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
          aria-label="Nach oben"
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          disabled={isLast || saving}
          onClick={() => onMoveDown(pos.id)}
          className="text-gray-300 hover:text-gray-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
          aria-label="Nach unten"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Header-Zeile: Klick togglet Beschreibung */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-start gap-1.5 w-full text-left rounded hover:bg-gray-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 -mx-1 px-1 py-0.5"
        >
          {pos.kind === 'subtotal' ? (
            <Sigma className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
          ) : pos.kind === 'note' ? (
            <StickyNote className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
          ) : pos.needsSapCreation ? (
            <PackagePlus className="w-3.5 h-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
          ) : (
            expanded
              ? <ChevronUp className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
              : <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
          )}
          <span
            className={cn(
              'leading-snug min-w-0 flex-1',
              pos.kind === 'note'
                ? 'font-semibold text-base text-gray-900'
                : 'text-small font-medium text-gray-800',
              expanded
                ? pos.kind === 'note'
                  ? 'whitespace-pre-wrap break-words'
                  : 'whitespace-normal break-words'
                : 'truncate',
            )}
            title={!expanded ? pos.description : undefined}
          >
            {expanded ? pos.description : descriptionTitle}
          </span>
        </button>

        {/* Collapsed preview of explanation text for note positions */}
        {!expanded && pos.kind === 'note' && pos.lineText?.trim() && (
          <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2 break-words leading-snug pl-5">
            {pos.lineText.trim()}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-1 mt-0.5">
          {pos.sapItemCode && (
            <span
              className={cn(
                'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono',
                pos.needsSapCreation
                  ? 'bg-amber-100 text-amber-800 border border-amber-300'
                  : 'bg-gray-100 text-gray-600',
              )}
              title={
                pos.needsSapCreation
                  ? `Artikel "${pos.sapItemCode}" existiert noch nicht in SAP. Bitte vor dem Export in SAP B1 anlegen.`
                  : undefined
              }
            >
              {pos.needsSapCreation && <PackagePlus className="w-3 h-3" />}
              {pos.sapItemCode}
            </span>
          )}
          {childCount > 0 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-brand-50 text-brand-700 border border-brand-200"
              title="Diese Position ist eine Stückliste — für den Kunden sichtbar, Details intern"
            >
              <Layers className="w-3 h-3" />
              Stückliste ({childCount} Pos.)
            </span>
          )}
          {isChild && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-gray-500 border border-gray-200"
              title="Teil einer Stückliste — nur intern sichtbar, nicht auf Kundendokument"
            >
              intern
            </span>
          )}
        </div>


        {expanded && pos.kind !== 'note' && pos.kind !== 'subtotal' && (
          <>
            <div className="grid grid-cols-3 gap-1 mt-1">
              {/* Quantity */}
              <div>
                <span className="block text-[10px] text-gray-400 mb-0.5">Menge</span>
                <InlineNumber
                  value={pos.quantity}
                  onChange={(v) => onUpdate(pos.id, { quantity: String(v) as unknown as number })}
                  min={0.01}
                  step={1}
                />
              </div>
              {/* Unit price */}
              <div>
                <span className="block text-[10px] text-gray-400 mb-0.5">Einzelpreis</span>
                <InlineNumber
                  value={pos.unitPrice !== null ? parseFloat(String(pos.unitPrice)).toFixed(2) : null}
                  onChange={(v) => onUpdate(pos.id, { unitPrice: String(v) as unknown as number })}
                  min={0}
                  step={0.01}
                  placeholder="—"
                />
              </div>
              {/* Discount */}
              <div>
                <span className="block text-[10px] text-gray-400 mb-0.5">Rabatt %</span>
                <InlineNumber
                  value={pos.discountPct !== null ? parseFloat(String(pos.discountPct ?? 0)) : 0}
                  onChange={(v) => onUpdate(pos.id, { discountPct: String(v) as unknown as number })}
                  min={0}
                  step={1}
                />
              </div>
            </div>
            {/* For BOM parents: offer to auto-calculate the package price from children */}
            {childCount > 0 && (pos.unitPrice === null || parseFloat(String(pos.unitPrice)) === 0) && (() => {
              const childrenSum = allPositions
                .filter((p) => p.parentPositionId === pos.id && p.kind !== 'note')
                .reduce((sum, p) => {
                  const qty = parseFloat(String(p.quantity || 0))
                  const price = parseFloat(String(p.unitPrice ?? 0))
                  const disc = parseFloat(String(p.discountPct ?? 0))
                  return sum + qty * price * (1 - disc / 100)
                }, 0)
              if (childrenSum <= 0) return null
              const qty = parseFloat(String(pos.quantity || 1))
              const perUnit = qty > 0 ? childrenSum / qty : childrenSum
              return (
                <button
                  type="button"
                  onClick={() => onUpdate(pos.id, { unitPrice: String(Math.round(perUnit * 100) / 100) as unknown as number })}
                  className="mt-1 text-[10px] text-brand-600 hover:text-brand-700 hover:underline"
                  title="Stückliste-Paketpreis muss auf der Vaterposition gesetzt sein — Einzelpositionen werden NICHT in SAP exportiert"
                >
                  Paketpreis aus {childCount} Pos. berechnen ({formatCurrency(perUnit)}/Stk.)
                </button>
              )
            })()}
          </>
        )}

        {/* Zeilenerweiterung (FreeText in SAP) */}
        {expanded && pos.kind !== 'note' && pos.kind !== 'subtotal' && (
          <div className="mt-2">
            <span className="block text-[10px] text-gray-400 mb-0.5">Text / Zeilenerweiterung</span>
            <InlineTextArea
              value={pos.lineText ?? ''}
              placeholder="Zusatztext zur Zeile (erscheint in SAP unter der Position)"
              onChange={(v) => onUpdate(pos.id, { lineText: v || null } as Partial<QuotePosition>)}
            />
          </div>
        )}

        {/* Text (lineText) for heading/note positions */}
        {expanded && pos.kind === 'note' && (
          <div className="mt-2">
            <span className="block text-[10px] text-gray-400 mb-0.5">Text</span>
            <InlineTextArea
              value={pos.lineText ?? ''}
              placeholder="Weitergehende Beschreibung (erscheint in SAP unter der Überschrift)"
              onChange={(v) => onUpdate(pos.id, { lineText: v || null } as Partial<QuotePosition>)}
            />
          </div>
        )}

        {/* Optional flag (SAP IsOptional) */}
        {expanded && pos.kind !== 'note' && pos.kind !== 'subtotal' && (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              id={`optional-${pos.id}`}
              checked={localOptional}
              onChange={(e) => {
                const checked = e.currentTarget.checked
                setLocalOptional(checked)
                onUpdate(pos.id, { isOptional: checked } as Partial<QuotePosition>)
              }}
              className="w-3.5 h-3.5 rounded border-gray-300 text-brand-500 focus:ring-brand-400 cursor-pointer"
            />
            <label
              htmlFor={`optional-${pos.id}`}
              className="text-[10px] text-gray-500 cursor-pointer select-none"
              title="Optionale Positionen erscheinen im SAP-Angebot, werden aber nicht in die Gesamtsumme eingerechnet"
            >
              Optional (in SAP nicht in Gesamtsumme)
            </label>
          </div>
        )}

        {/* Parent assignment: allows retroactively nesting under a BOM position.
            Only visible when expanded AND there are possible parents (notes + self filtered out). */}
        {expanded && pos.kind !== 'note' && pos.kind !== 'subtotal' && parentCandidates.length > 0 && (
          <div className="mt-2">
            <span className="block text-[10px] text-gray-400 mb-0.5">Teil einer Stückliste</span>
            <select
              value={pos.parentPositionId ?? ''}
              onChange={(e) => onUpdate(pos.id, { parentPositionId: e.currentTarget.value || null } as Partial<QuotePosition>)}
              className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-brand-400"
            >
              <option value="">— eigenständig —</option>
              {parentCandidates.map((parent) => (
                <option key={parent.id} value={parent.id}>
                  {parent.sapItemCode ?? (parent.description?.split('\n')[0] ?? '').slice(0, 40)}
                </option>
              ))}
            </select>
          </div>
        )}

        {!expanded && pos.kind !== 'note' && pos.kind !== 'subtotal' && (
          <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-0.5 flex-wrap">
            <span>{parseFloat(String(pos.quantity || 0))} ×</span>
            <span>{pos.unitPrice !== null ? formatCurrency(pos.unitPrice) : '—'}</span>
            {pos.discountPct !== null && Number(pos.discountPct) > 0 && (
              <span>−{Number(pos.discountPct)}%</span>
            )}
            {localOptional && (
              <span className="px-1 rounded bg-blue-50 text-blue-600 font-medium">opt.</span>
            )}
            {/* BOM parent with no price: show recalculate shortcut even when collapsed */}
            {childCount > 0 && (pos.unitPrice === null || parseFloat(String(pos.unitPrice)) === 0) && (() => {
              const childrenSum = allPositions
                .filter((p) => p.parentPositionId === pos.id && p.kind !== 'note')
                .reduce((sum, p) => {
                  const qty = parseFloat(String(p.quantity || 0))
                  const price = parseFloat(String(p.unitPrice ?? 0))
                  const disc = parseFloat(String(p.discountPct ?? 0))
                  return sum + qty * price * (1 - disc / 100)
                }, 0)
              if (childrenSum <= 0) return null
              const qty = parseFloat(String(pos.quantity || 1))
              const perUnit = qty > 0 ? childrenSum / qty : childrenSum
              return (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onUpdate(pos.id, { unitPrice: String(Math.round(perUnit * 100) / 100) as unknown as number }) }}
                  className="text-[10px] text-brand-600 hover:text-brand-700 hover:underline"
                  title="Gesamtpreis der Kindpositionen auf die Paketposition übertragen"
                >
                  ∑ {formatCurrency(perUnit)}/Stk. übernehmen
                </button>
              )
            })()}
          </div>
        )}
      </div>

      {/* Line total + delete */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0 pt-0.5 min-w-[4.5rem]">
        {pos.kind === 'subtotal' ? (
          <span className="text-small font-semibold text-blue-700 tabular-nums">
            {formatCurrency(calcSubtotalAmount(pos, allPositions))}
          </span>
        ) : lineTotal !== null ? (
          <span className="text-small font-semibold text-gray-900 tabular-nums">
            {formatCurrency(lineTotal)}
          </span>
        ) : (
          pos.kind !== 'note' && (
            <span className="text-small text-gray-400">—</span>
          )
        )}
        <button
          type="button"
          onClick={() => onDelete(pos.id)}
          disabled={saving}
          className="text-gray-300 hover:text-red-500 disabled:opacity-30 transition-colors"
          aria-label="Position entfernen"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
})

// ─── Sortable group wrapper ───────────────────────────────────────────────────
// Wraps a top-level position and its children as a single sortable unit.
// When the parent is dragged, the entire group (parent + children) moves together.

interface SortablePositionGroupProps {
  parent: QuotePosition
  groupChildren: QuotePosition[]
  parentFlatIdx: number
  totalFlatCount: number
  onUpdate: (posId: string, patch: Partial<QuotePosition>) => void
  onDelete: (posId: string) => void
  onMoveUp: (posId: string) => void
  onMoveDown: (posId: string) => void
  savingId: string | null
  allPositions: QuotePosition[]
}

const SortablePositionGroup = memo(function SortablePositionGroup({
  parent,
  groupChildren,
  parentFlatIdx,
  totalFlatCount,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  savingId,
  allPositions,
}: SortablePositionGroupProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: parent.id,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const lastFlatIdxInGroup = parentFlatIdx + groupChildren.length

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={isDragging ? 'opacity-40' : undefined}
    >
      <PositionRow
        pos={parent}
        isFirst={parentFlatIdx === 0}
        isLast={lastFlatIdxInGroup === totalFlatCount - 1}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        savingId={savingId}
        isChild={false}
        allPositions={allPositions}
        childCount={groupChildren.length}
        dragHandleListeners={listeners}
      />
      {groupChildren.map((child, childIdx) => (
        <PositionRow
          key={child.id}
          pos={child}
          isFirst={false}
          isLast={parentFlatIdx + 1 + childIdx === totalFlatCount - 1}
          onUpdate={onUpdate}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          savingId={savingId}
          isChild={true}
          allPositions={allPositions}
          childCount={0}
        />
      ))}
    </div>
  )
})

// ─── Main component ───────────────────────────────────────────────────────────

// ─── Servicekatalog Quick-Add Modal ──────────────────────────────────────────

interface ServiceCatalogItem {
  id: string
  title: string
  description?: string
  priceCents: number
  timeHours: number
  unit: string
  sapItemCode?: string
  categoryName?: string
}

function ServicekatalogQuickAddModal({
  quoteId,
  onAdded,
  onClose,
}: {
  quoteId: string
  onAdded: () => void
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<ServiceCatalogItem[]>([])
  const [searching, setSearching] = useState(false)
  const [adding, setAdding] = useState<string | null>(null)
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set())
  const [showItemFormModal, setShowItemFormModal] = useState(false)

  const { data: categories = [] } = useQuery<ServiceCatalogCategory[]>({
    queryKey: ['service-catalog-categories'],
    queryFn: () => apiClient.get('/service-catalog/categories').then((r) => r.data),
    retry: false,
  })
  const { data: settings } = useQuery<{ defaultUnit: string; syncTenantIds: string[] }>({
    queryKey: ['service-catalog-settings'],
    queryFn: () => apiClient.get('/service-catalog/settings').then((r) => r.data),
    retry: false,
  })
  const defaultUnit = settings?.defaultUnit ?? 'Stk'
  const hasSyncTenants = (settings?.syncTenantIds ?? []).length > 0

  useEffect(() => {
    if (!search.trim()) { setResults([]); return }
    const timer = setTimeout(async () => {
      setSearching(true)
      try {
        const res = await apiClient.get('/service-catalog/search', { params: { q: search, limit: '20' } })
        setResults(res.data)
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  async function handleAdd(item: ServiceCatalogItem) {
    setAdding(item.id)
    try {
      await createQuotePosition(quoteId, {
        description: item.title,
        sapItemCode: item.sapItemCode ?? undefined,
        quantity: item.timeHours > 0 ? item.timeHours : 1,
        unitPrice: item.priceCents / 100,
        kind: 'service',
      })
      setAddedIds((prev) => new Set([...prev, item.id]))
      onAdded()
    } catch {
      // silent — user can retry
    } finally {
      setAdding(null)
    }
  }

  function handleCreatedAndAdd(item: FullCatalogItem) {
    onClose()
    void createQuotePosition(quoteId, {
      description: item.title,
      sapItemCode: item.sapItemCode ?? undefined,
      quantity: item.timeHours > 0 ? item.timeHours : 1,
      unitPrice: item.priceCents / 100,
      kind: 'service',
    }).then(() => {
      onAdded()
    }).catch(() => {
      // silent
    })
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[70vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 p-4 border-b border-gray-200">
          <Book className="w-5 h-5 text-brand-500 shrink-0" />
          <h2 className="text-sm font-semibold text-gray-900 flex-1">Servicekatalog</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowItemFormModal(true)}
            title="Neuen Artikel anlegen"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Neuer Artikel
          </Button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Leistung suchen…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {results.length === 0 && search.trim() && !searching && (
            <p className="text-sm text-gray-500 text-center py-6">Keine Einträge gefunden</p>
          )}
          {!search.trim() && (
            <p className="text-xs text-gray-400 text-center py-6">Suchbegriff eingeben…</p>
          )}
          {results.map((item) => (
            <div
              key={item.id}
              className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-0"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {item.categoryName && (
                    <span className="text-xs text-blue-600">{item.categoryName}</span>
                  )}
                  <span className="text-xs text-green-700 font-semibold">
                    {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(item.priceCents / 100)}
                  </span>
                  {item.timeHours > 0 && (
                    <span className="text-xs text-gray-500">{item.timeHours} {item.unit}</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleAdd(item)}
                disabled={adding === item.id || addedIds.has(item.id)}
                className={cn(
                  'shrink-0 flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors',
                  addedIds.has(item.id)
                    ? 'bg-green-100 text-green-700 cursor-default'
                    : 'bg-brand-100 text-brand-700 hover:bg-brand-200',
                )}
              >
                {adding === item.id
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : addedIds.has(item.id)
                    ? '✓ Hinzugefügt'
                    : <><Plus className="w-3 h-3" /> Hinzufügen</>
                }
              </button>
            </div>
          ))}
        </div>
      </div>

      <ItemFormModal
        open={showItemFormModal}
        onOpenChange={setShowItemFormModal}
        item={null}
        categories={categories}
        hasSyncTenants={hasSyncTenants}
        defaultUnit={defaultUnit}
        onSaved={() => setShowItemFormModal(false)}
        onCreatedAndAdd={handleCreatedAndAdd}
      />
    </div>
  )
}

// ─── QuoteSidebar ─────────────────────────────────────────────────────────────

export interface QuoteSidebarProps {
  quoteId: string
  positions: QuotePosition[]
  loading: boolean
  error: string | null
  onRefetch: () => void
}

export const QuoteSidebar = memo(function QuoteSidebar({ quoteId, positions, loading, error, onRefetch }: QuoteSidebarProps) {
  // Per-position saving state: only the row being saved shows the disabled/spinner state.
  // This prevents ALL rows from re-rendering when a single save starts.
  const [savingId, setSavingId] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [showServiceCatalogModal, setShowServiceCatalogModal] = useState(false)

  // State for the inline BOM group creation form
  const [showBomForm, setShowBomForm] = useState(false)
  const [bomLabel, setBomLabel] = useState('')
  const [bomCreating, setBomCreating] = useState(false)

  // Guard: track which BOM parents have already been auto-priced so we don't
  // fire the API call multiple times during intermediate renders.
  const autoPricedRef = useRef(new Set<string>())

  // Stable ref to positions — lets callbacks read the current positions without
  // listing `positions` as a dep (which would recreate all callbacks on every refetch).
  const positionsRef = useRef(positions)
  positionsRef.current = positions

  // dnd-kit: active drag item ID (the top-level position being dragged)
  const [activeId, setActiveId] = useState<string | null>(null)

  // dnd-kit sensors — PointerSensor handles mouse + touch; KeyboardSensor for accessibility
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  async function handleCreateBomGroup() {
    const label = bomLabel.trim() || 'Stückliste'
    setBomCreating(true)
    setSaveError(null)
    try {
      await createQuotePosition(quoteId, {
        description: label,
        kind: 'article',
        quantity: 1,
        unitPrice: null,
      })
      setShowBomForm(false)
      setBomLabel('')
      onRefetch()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Stückliste konnte nicht erstellt werden')
    } finally {
      setBomCreating(false)
    }
  }

  // Auto-calculate and save the bundle price for BOM parents that have no price yet.
  // Fires once per parent (guarded by autoPricedRef) so it doesn't loop.
  useEffect(() => {
    if (positions.length === 0) return

    const childrenByParent = new Map<string, QuotePosition[]>()
    for (const p of positions) {
      if (p.parentPositionId) {
        const arr = childrenByParent.get(p.parentPositionId) ?? []
        arr.push(p)
        childrenByParent.set(p.parentPositionId, arr)
      }
    }

    for (const pos of positions) {
      if (pos.parentPositionId) continue
      // Skip if a real (non-zero) price is already set — don't overwrite user edits.
      if (pos.unitPrice !== null && parseFloat(String(pos.unitPrice)) > 0) continue
      const children = childrenByParent.get(pos.id) ?? []
      if (children.length === 0) continue
      if (autoPricedRef.current.has(pos.id)) continue

      const childrenSum = children
        .filter((p) => p.kind !== 'note' && p.kind !== 'subtotal')
        .reduce((sum, p) => {
          const qty = parseFloat(String(p.quantity || 0))
          const price = parseFloat(String(p.unitPrice ?? 0))
          const disc = parseFloat(String(p.discountPct ?? 0))
          return sum + qty * price * (1 - disc / 100)
        }, 0)

      if (childrenSum <= 0) continue

      const qty = parseFloat(String(pos.quantity || 1))
      const perUnit = qty > 0 ? childrenSum / qty : childrenSum
      const rounded = Math.round(perUnit * 100) / 100

      autoPricedRef.current.add(pos.id)
      void updateQuotePosition(quoteId, pos.id, { unitPrice: String(rounded) } as Record<string, unknown>).then(() => {
        onRefetch()
      })
    }
  }, [positions, quoteId, onRefetch])

  const handleUpdate = useCallback(
    async (positionId: string, patch: Partial<QuotePosition>) => {
      setSavingId(positionId)
      setSaveError(null)
      try {
        await updateQuotePosition(quoteId, positionId, patch as Record<string, unknown>)
        onRefetch()
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen')
        onRefetch()
      } finally {
        setSavingId(null)
      }
    },
    [quoteId, onRefetch],
  )

  const handleDelete = useCallback(
    async (positionId: string) => {
      setSavingId(positionId)
      setSaveError(null)
      try {
        await deleteQuotePosition(quoteId, positionId)
        onRefetch()
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen')
        onRefetch()
      } finally {
        setSavingId(null)
      }
    },
    [quoteId, onRefetch],
  )

  const handleMoveUp = useCallback(
    async (positionId: string) => {
      const positions = positionsRef.current
      const idx = positions.findIndex((p) => p.id === positionId)
      if (idx <= 0) return
      const above = positions[idx - 1]
      await Promise.all([
        updateQuotePosition(quoteId, positionId, { sortIndex: above.sortIndex }),
        updateQuotePosition(quoteId, above.id, { sortIndex: positions[idx].sortIndex }),
      ]).catch((err) => setSaveError(err instanceof Error ? err.message : 'Reihenfolge konnte nicht geändert werden'))
      onRefetch()
    },
    [quoteId, onRefetch],
  )

  const handleMoveDown = useCallback(
    async (positionId: string) => {
      const positions = positionsRef.current
      const idx = positions.findIndex((p) => p.id === positionId)
      if (idx < 0 || idx >= positions.length - 1) return
      const below = positions[idx + 1]
      await Promise.all([
        updateQuotePosition(quoteId, positionId, { sortIndex: below.sortIndex }),
        updateQuotePosition(quoteId, below.id, { sortIndex: positions[idx].sortIndex }),
      ]).catch((err) => setSaveError(err instanceof Error ? err.message : 'Reihenfolge konnte nicht geändert werden'))
      onRefetch()
    },
    [quoteId, onRefetch],
  )

  // dnd-kit drag reorder: rebuilds sortIndex for all top-level positions
  const handleDragReorder = useCallback(
    async (fromId: string, toId: string) => {
      const topLevel = positionsRef.current.filter((p) => !p.parentPositionId)
      const fromIdx = topLevel.findIndex((p) => p.id === fromId)
      const toIdx = topLevel.findIndex((p) => p.id === toId)
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return
      const reordered = [...topLevel]
      const [moved] = reordered.splice(fromIdx, 1)
      reordered.splice(toIdx, 0, moved!)
      setSavingId(fromId)
      setSaveError(null)
      try {
        await Promise.all(
          reordered.map((p, newIdx) =>
            p.sortIndex !== newIdx
              ? updateQuotePosition(quoteId, p.id, { sortIndex: newIdx })
              : Promise.resolve(),
          ),
        )
        onRefetch()
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Reihenfolge konnte nicht geändert werden')
        onRefetch()
      } finally {
        setSavingId(null)
      }
    },
    [quoteId, onRefetch],
  )

  const handleDndStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id))
  }, [])

  const handleDndEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)
    if (!over || active.id === over.id) return
    await handleDragReorder(String(active.id), String(over.id))
  }, [handleDragReorder])

  const netTotal = useMemo(() => calcNetTotal(positions), [positions])
  const needsCreationCount = useMemo(() => positions.filter((p) => p.needsSapCreation).length, [positions])

  // Build the group structure: each top-level position + its children as one sortable unit.
  // Memoized so that unrelated state changes (bomLabel, savingId, etc.) don't trigger recomputation.
  const { groups, totalFlatCount, sortableIds, childrenByParent } = useMemo(() => {
    const childrenByParent = new Map<string, QuotePosition[]>()
    for (const p of positions) {
      if (p.parentPositionId) {
        const arr = childrenByParent.get(p.parentPositionId) ?? []
        arr.push(p)
        childrenByParent.set(p.parentPositionId, arr)
      }
    }

    const topLevel = positions.filter((p) => !p.parentPositionId)
    const orphanChildren = positions.filter(
      (p) => p.parentPositionId && !positions.some((q) => q.id === p.parentPositionId),
    )
    const orderedTop = [...topLevel, ...orphanChildren]

    let currentFlatIdx = 0
    const groups = orderedTop.map(parent => {
      const children = (childrenByParent.get(parent.id) ?? []).sort((a, b) => a.sortIndex - b.sortIndex)
      const parentFlatIdx = currentFlatIdx
      currentFlatIdx += 1 + children.length
      return { parent, children, parentFlatIdx }
    })

    return {
      groups,
      totalFlatCount: currentFlatIdx,
      sortableIds: orderedTop.map(p => p.id),
      childrenByParent,
    }
  }, [positions])

  // The active position for the DragOverlay preview
  const activePosition = activeId ? positions.find(p => p.id === activeId) : null
  const activeChildCount = activeId ? (childrenByParent.get(activeId)?.length ?? 0) : 0

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayoutList className="w-4 h-4 text-gray-400" />
            <span className="text-small font-semibold text-gray-700">Positionen</span>
            {positions.length > 0 && (
              <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                {positions.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {savingId !== null && (
              <span className="text-[10px] text-gray-400 italic">Speichern…</span>
            )}
            {!showBomForm && (
              <>
                <button
                  type="button"
                  title="Aus Servicekatalog hinzufügen"
                  onClick={() => setShowServiceCatalogModal(true)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-green-600 hover:bg-green-50 transition-colors"
                >
                  <Book className="w-3 h-3" />
                  <Plus className="w-2.5 h-2.5" />
                </button>
                <button
                  type="button"
                  title="Stückliste erstellen (Positionen für Kunden zusammenfassen)"
                  onClick={() => setShowBomForm(true)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-brand-600 hover:bg-brand-50 transition-colors"
                >
                  <Layers className="w-3 h-3" />
                  <Plus className="w-2.5 h-2.5" />
                </button>
              </>
            )}
          </div>
        </div>
        {/* Inline form to create a BOM group */}
        {showBomForm && (
          <div className="mt-2 flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-brand-600 flex-shrink-0" />
            <input
              type="text"
              autoFocus
              placeholder="Bezeichnung für Kunden (z.B. 4x Laptop HP 14&quot;)"
              value={bomLabel}
              onChange={(e) => setBomLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleCreateBomGroup()
                if (e.key === 'Escape') { setShowBomForm(false); setBomLabel('') }
              }}
              className="flex-1 rounded border border-brand-300 px-2 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
            <button
              type="button"
              onClick={() => void handleCreateBomGroup()}
              disabled={bomCreating}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {bomCreating ? '…' : 'Erstellen'}
            </button>
            <button
              type="button"
              onClick={() => { setShowBomForm(false); setBomLabel('') }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Abbrechen"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Error banner */}
      {(saveError || error) && (
        <div className="px-3 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between gap-2">
          <p className="text-[11px] text-red-700 min-w-0 truncate">{saveError ?? error}</p>
          <Button variant="ghost" size="xs" onClick={() => { setSaveError(null); onRefetch() }}>
            Wiederholen
          </Button>
        </div>
      )}

      {/* Position list */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {loading ? (
          <div className="flex items-center justify-center h-full py-8">
            <LoadingState label="Positionen werden geladen…" />
          </div>
        ) : error && positions.length === 0 ? (
          <div className="flex items-center justify-center h-full py-8 px-4">
            <ErrorState
              title="Positionen konnten nicht geladen werden"
              description={error}
            />
          </div>
        ) : positions.length === 0 ? (
          <div className="flex items-center justify-center h-full py-8 px-4">
            <EmptyState
              title="Noch keine Positionen"
              description="Bitte die KI um konkrete Artikel oder Dienstleistungen."
            />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDndStart}
            onDragEnd={handleDndEnd}
          >
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              <div>
                {groups.map((group) => (
                  <SortablePositionGroup
                    key={group.parent.id}
                    parent={group.parent}
                    groupChildren={group.children}
                    parentFlatIdx={group.parentFlatIdx}
                    totalFlatCount={totalFlatCount}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    savingId={savingId}
                    allPositions={positions}
                  />
                ))}
              </div>
            </SortableContext>
            {/* DragOverlay renders the dragged item as a ghost following the cursor */}
            <DragOverlay>
              {activePosition ? (
                <div className="bg-white rounded border border-brand-300 shadow-xl px-3 py-2 flex items-center gap-2 opacity-95">
                  <GripVertical className="w-4 h-4 text-brand-400 flex-shrink-0" />
                  <span className="text-small font-medium text-gray-800 truncate max-w-[200px]">
                    {(activePosition.description?.split('\n')[0] ?? '').trim() || '—'}
                  </span>
                  {activeChildCount > 0 && (
                    <span className="text-[10px] text-brand-600 flex-shrink-0 ml-1">
                      +{activeChildCount} Pos.
                    </span>
                  )}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Footer: net total */}
      {positions.length > 0 && (
        <div className="flex-shrink-0 border-t border-gray-200 px-4 py-3 bg-gray-50">
          {needsCreationCount > 0 && (
            <div className="mb-2 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5">
              <PackagePlus className="w-3.5 h-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] leading-snug text-amber-800">
                {needsCreationCount === 1
                  ? '1 Artikel muss vor dem SAP-Export in SAP B1 angelegt werden.'
                  : `${needsCreationCount} Artikel müssen vor dem SAP-Export in SAP B1 angelegt werden.`}
              </p>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-small text-gray-600">Netto gesamt</span>
            <span className="text-body font-bold text-gray-900 tabular-nums">
              {formatCurrency(netTotal)}
            </span>
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5">zzgl. MwSt. · {positions.length} Position{positions.length !== 1 ? 'en' : ''}</p>
        </div>
      )}

      {showServiceCatalogModal && (
        <ServicekatalogQuickAddModal
          quoteId={quoteId}
          onAdded={onRefetch}
          onClose={() => setShowServiceCatalogModal(false)}
        />
      )}
    </div>
  )
})
